import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  BarChart2,
  Bell,
  ChefHat,
  Cloud,
  Clock,
  Download,
  GlassWater,
  Home,
  Image,
  LogOut,
  Lock,
  MoreHorizontal,
  Moon,
  Package,
  Receipt,
  ShoppingCart,
  Shield,
  Settings,
  ShoppingBag,
  Sun,
  TrendingUp,
  User,
  Users,
  X,
} from "lucide-react";
import { APP_VERSION } from "../version";
import { clearLockedSession, setLockedSession } from "../utils/lockedSessions";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { apiFetch } from "../utils/apiClient";
import { canUseCashSessionForRole } from "../utils/cashSessionRoles";
import { showToast } from "../utils/toast";

const getPageIdFromPath = (path) => {
  if (path.includes("/sales")) return "sales";
  if (path.includes("/products")) return "products";
  if (path.includes("/categories")) return "categories";
  if (path.includes("/medias")) return "medias";
  if (path.includes("/users")) return "users";
  if (path.includes("/permissions")) return "permissions";
  if (path.includes("/customers")) return "customers";
  if (path.includes("/tables")) return "tables";
  if (path.includes("/sessions")) return "sessions";
  if (path.includes("/waise") || path.includes("/keassa")) return "waise";
  if (path.includes("/reports")) return "reports";
  if (path.includes("/settings")) return "settings";
  return "dashboard";
};

const NAV_SECTIONS = [
  {
    title: "Vue générale",
    items: [
      { to: "/admin", label: "Tableau de bord", icon: Home, end: true },
      { to: "/admin/sales", label: "Ventes", icon: TrendingUp },
    ],
  },
  {
    title: "Gestion",
    items: [
      { to: "/admin/products", label: "Produits", icon: ShoppingBag },
      { to: "/admin/categories", label: "Catégories", icon: ChefHat },
      { to: "/admin/medias", label: "Médias", icon: Image },
      { to: "/admin/users", label: "Utilisateurs", icon: Users },
      {
        to: "/admin/permissions",
        label: "Permissions",
        icon: Shield,
      },
      { to: "/admin/customers", label: "Clients", icon: User },
      { to: "/admin/tables", label: "Tables", icon: GlassWater },
      { to: "/admin/sessions", label: "Sessions", icon: Clock },
    ],
  },
  {
    title: "Modules avancés",
    items: [
      {
        to: "/admin/waise",
        label: "Waise",
        icon: Package,
      },
    ],
  },
  {
    title: "Analyse & système",
    items: [
      { to: "/admin/reports", label: "Statistiques", icon: BarChart2, end: true },
      { to: "/admin/reports/caisse", label: "Rapports de caisse", icon: Receipt, end: true },
      { to: "/admin/settings", label: "Paramètres", icon: Settings },
    ],
  },
];

const buildNavSections = (basePath, allowedPages, options = {}) => {
  const { hideUsers = false, hideTables = false } = options;
  const normalizedAllowedPages = Array.isArray(allowedPages)
    ? allowedPages.map((page) => (page === "keassa" ? "waise" : page))
    : allowedPages;
  const filteredNav = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .map((item) => ({
        ...item,
        to: item.to.replace("/admin", basePath),
      }))
      .filter((item) => {
        const pageId = getPageIdFromPath(item.to);
        if (hideUsers && pageId === "users") return false;
        if (hideTables && pageId === "tables") return false;
        return true;
      }),
  }));
  if (!normalizedAllowedPages || normalizedAllowedPages.length === 0) return filteredNav;
  return filteredNav
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        normalizedAllowedPages.includes(getPageIdFromPath(item.to)),
      ),
    }))
    .filter((section) => section.items.length > 0);
};

const PAGE_META = [
  {
    match: (path) =>
      path === "/admin" ||
      path === "/admin/" ||
      path === "/superadmin" ||
      path === "/superadmin/",
    title: "Administration MOO Sync",
    subtitle: "Vue d'ensemble temps réel de vos opérations",
    kicker: "Console",
  },
  {
    match: (path) => path.startsWith("/admin/sales") || path.startsWith("/superadmin/sales"),
    title: "Suivi des ventes",
    subtitle: "Analyse des tickets, paiements et performances",
    kicker: "Ventes",
  },
  {
    match: (path) => path.startsWith("/admin/products") || path.startsWith("/superadmin/products"),
    title: "Catalogue produits",
    subtitle: "Structurez le menu, les suppléments et destinations",
    kicker: "Gestion",
  },
  {
    match: (path) => path.startsWith("/admin/categories") || path.startsWith("/superadmin/categories"),
    title: "Catégories & familles",
    subtitle: "Organisez vos plats par pôles opérationnels",
    kicker: "Gestion",
  },
  {
    match: (path) => path.startsWith("/admin/medias") || path.startsWith("/superadmin/medias"),
    title: "Bibliothèque média",
    subtitle: "Centralisez les visuels utilisés sur le menu digital",
    kicker: "Branding",
  },
  {
    match: (path) => path.startsWith("/admin/users") || path.startsWith("/superadmin/users"),
    title: "Équipe & accès",
    subtitle: "Pilotez les rôles serveurs, caissiers et managers",
    kicker: "Utilisateurs",
  },
  {
    match: (path) => path.startsWith("/admin/permissions") || path.startsWith("/superadmin/permissions"),
    title: "Permissions",
    subtitle: "Remises et annulations : droits par rôle",
    kicker: "Accès",
  },
  {
    match: (path) => path.startsWith("/admin/customers") || path.startsWith("/superadmin/customers"),
    title: "Clients & CRM",
    subtitle: "Historique, fidélité et préférences clients",
    kicker: "CRM",
  },
  {
    match: (path) => path.startsWith("/admin/tables") || path.startsWith("/superadmin/tables"),
    title: "Plan des tables",
    subtitle: "Suivez les zones, capacités et disponibilités",
    kicker: "Salle",
  },
  {
    match: (path) => path.startsWith("/admin/sessions") || path.startsWith("/superadmin/sessions"),
    title: "Sessions de vente",
    subtitle: "Ouvertures, clôtures et fonds de caisse",
    kicker: "Caisse",
  },
  {
    match: (path) =>
      path.startsWith("/admin/waise") ||
      path.startsWith("/superadmin/waise") ||
      path.startsWith("/admin/keassa") ||
      path.startsWith("/superadmin/keassa"),
    title: "WAISE",
    subtitle: "Stocks, achats, inventaires et pertes",
    kicker: "Gestion opérationnelle",
  },
  {
    match: (path) =>
      path.startsWith("/admin/reports/caisse") || path.startsWith("/superadmin/reports/caisse"),
    title: "Rapports de caisse",
    subtitle: "Rapports X/Z, sessions et journaux de déconnexion",
    kicker: "Caisse",
  },
  {
    match: (path) => path.startsWith("/admin/reports") || path.startsWith("/superadmin/reports"),
    title: "Statistiques",
    subtitle: "Analyse des ventes, produits et tendances",
    kicker: "Statistiques",
  },
  {
    match: (path) => path.startsWith("/admin/settings") || path.startsWith("/superadmin/settings"),
    title: "Paramètres système",
    subtitle: "Configuration avancée, imprimantes, intégrations",
    kicker: "Système",
  },
];

const DEFAULT_META = {
  title: "Administration Moo Sync",
  subtitle: "Pilotage centralisé des opérations",
  kicker: "Administration",
};

const LOGO_SRC = "/moo-sync-insigne-logo.png";
const ADMIN_THEME_STORAGE_KEY = "moo_admin_theme";
const ADMIN_THEME_USER_STORAGE_KEY = "moo_admin_theme_user";

const normalizeTheme = (value) => {
  if (value === "dark" || value === "light") return value;
  return null;
};

const resolveInitialAdminTheme = () => {
  if (typeof window === "undefined") return "dark";
  try {
    const storedTheme = normalizeTheme(localStorage.getItem(ADMIN_THEME_STORAGE_KEY));
    const isUserSet = localStorage.getItem(ADMIN_THEME_USER_STORAGE_KEY) === "1";
    if (isUserSet && storedTheme) return storedTheme;
    return "dark";
  } catch (_e) {
    return "dark";
  }
};

const resolvePageMeta = (pathname) => {
  const meta = PAGE_META.find((entry) => entry.match(pathname));
  return meta || DEFAULT_META;
};

const isIosDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator?.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isPwaStandalone = () => {
  if (typeof window === "undefined") return false;
  const mediaStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const safariStandalone = window.navigator?.standalone === true;
  return Boolean(mediaStandalone || safariStandalone);
};

const isCloudTenantHostname = () => {
  if (typeof window === "undefined") return false;
  try {
    const host = window.location?.hostname || "";
    if (!host) return false;
    if (host === "localhost" || host === "127.0.0.1" || host === "::1") return false;
    return host.includes("moosync");
  } catch (_e) {
    return false;
  }
};

const AdminSidebar = ({
  collapsed,
  theme,
  onToggleCollapse,
  onToggleTheme,
  onLogout,
  onLockSession,
  logoSrc = LOGO_SRC,
  currentUser,
  allowedPages,
  basePath,
  showCaisseShortcut = false,
  hideUsers = false,
  hideTables = false,
}) => {
  const navigate = useNavigate();

  const userInitials = useMemo(() => {
    if (!currentUser) return "MS";
    const { first_name = "", last_name = "" } = currentUser;
    return `${first_name.charAt(0) || ""}${last_name.charAt(0) || ""}`.trim() || "MS";
  }, [currentUser]);

  const sidebarBg =
    theme === "dark" ? "bg-[#0d1524]/90 border-white/10" : "bg-white border-gray-100";
  const sidebarText =
    theme === "dark" ? "text-slate-100" : "text-slate-700";

  const computedSections = useMemo(
    () => buildNavSections(basePath, allowedPages, { hideUsers, hideTables }),
    [basePath, allowedPages, hideUsers, hideTables],
  );

  return (
    <aside
      className={`relative z-40 h-full border-r ${sidebarBg} ${sidebarText} transition-all duration-300 ${
        collapsed ? "w-20" : "w-72"
      }`}
    >
      <div className="flex h-full flex-col">
        <div
          className={
            collapsed
              ? "flex flex-col items-center space-y-3 px-2 py-5"
              : "flex items-center justify-between px-4 py-5"
          }
        >
          <div
            className={
              collapsed
                ? "flex flex-col items-center space-y-2"
                : "flex items-center space-x-3"
            }
          >
            <img
              src={logoSrc}
              alt="Logo MOO Sync"
              className="h-9 w-9 rounded-2xl border border-white/20 bg-white/10 object-contain p-1 shadow-lg"
            />
            <div
              className={
                collapsed
                  ? "sr-only"
                  : "text-lg font-semibold tracking-wide"
              }
            >
              MOO Sync
            </div>
          </div>
          <button
            onClick={onToggleCollapse}
            className={`group relative rounded-full p-2 text-sm transition focus:ring-2 focus:ring-yellow-400 ${
              theme === "dark"
                ? "border border-white/30 bg-white/10 text-white hover:bg-white/20"
                : "border border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            aria-label="Toggle sidebar"
          >
            <ArrowLeftRight
              size={18}
              className={`transition group-hover:scale-105 ${
                theme === "dark" ? "drop-shadow-lg" : ""
              }`}
            />
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/80 px-3 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">
              {collapsed ? "Ouvrir" : "Replier"}
            </span>
          </button>
        </div>

        {currentUser && (
          <div className="mx-4 mb-4 rounded-2xl border border-white/10 bg-gradient-to-br from-yellow-500/90 to-orange-500/90 p-3 text-white shadow-lg">
            <div className="flex w-full items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 text-base font-semibold uppercase">
                {userInitials}
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="text-sm font-semibold">
                    {currentUser.first_name} {currentUser.last_name}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-white/80">
                    {currentUser.role}
                  </p>
                </div>
              )}
              {showCaisseShortcut && (
                <button
                  type="button"
                  onClick={() => {
                    localStorage.setItem("moo_admin_cash_drawer", "1");
                    navigate("/caisse");
                  }}
                  className="ml-auto hidden h-10 w-10 items-center justify-center rounded-xl border border-white/20 bg-white/15 text-white transition hover:bg-white/25 md:inline-flex"
                  title="Aller à la caisse"
                  aria-label="Aller à la caisse"
                >
                  <ShoppingCart size={18} />
                </button>
              )}
            </div>
          </div>
        )}

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 pb-6">
          {computedSections.map((section) => (
            <div key={section.title}>
              {!collapsed && (
                <p className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {section.title}
                </p>
              )}
              <div className="mt-2 space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [
                        "group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                        isActive
                          ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg shadow-orange-500/30"
                          : theme === "dark"
                            ? "text-slate-300 hover:bg-white/5"
                            : "text-slate-600 hover:bg-slate-100",
                      ].join(" ")
                    }
                  >
                    <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                    {!collapsed && (
                      <span className="flex-1">{item.label}</span>
                    )}
                    {!collapsed && item.badge && (
                      <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        {item.badge}
                      </span>
                    )}
                    {collapsed && (
                      <span className="sr-only">{item.label}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/5 p-4">
          <button
            onClick={onToggleTheme}
            className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm font-semibold transition shadow-inner ${
              theme === "dark"
                ? "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200"
            }`}
          >
            <span className={`${collapsed ? "sr-only" : ""}`}>
              Mode {theme === "dark" ? "clair" : "sombre"}
            </span>
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onLogout}
              className="flex-1 flex items-center justify-center rounded-2xl border border-red-300/40 bg-red-500/10 px-3 py-2.5 text-sm font-semibold text-red-500 transition hover:bg-red-500/20 hover:text-white"
            >
              <LogOut size={16} className={collapsed ? "" : "mr-2"} />
              <span className={collapsed ? "sr-only" : ""}>Déconnexion</span>
            </button>
            <button
              type="button"
              onClick={onLockSession}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white/85 transition hover:bg-white/20"
              title="Verrouiller la session"
              aria-label="Verrouiller la session"
            >
              <Lock size={16} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

const AdminLayout = () => {
  const isSuperAdminPath =
    typeof window !== "undefined" && window.location.pathname.startsWith("/superadmin");
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      if (isSuperAdminPath) {
        const superStored = localStorage.getItem("moo_user_superadmin");
        return superStored ? JSON.parse(superStored) : null;
      }
      const stored = localStorage.getItem("moo_user");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const [allowedPages, setAllowedPages] = useState(null);
  const basePath = isSuperAdminPath
    ? "/superadmin"
    : currentUser?.role === "superadmin"
      ? "/superadmin"
      : "/admin";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(resolveInitialAdminTheme);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutDialogLoading, setLogoutDialogLoading] = useState(false);
  const [logoutConfirmLoading, setLogoutConfirmLoading] = useState(false);
  const [logoutSalesWarning, setLogoutSalesWarning] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const root = document.documentElement;
    try {
      localStorage.setItem(ADMIN_THEME_STORAGE_KEY, theme);
    } catch (_e) {
      // ignore storage errors (private mode / quota / sandbox)
    }
    root.classList.toggle("dark", theme === "dark");
    root.style.colorScheme = theme === "dark" ? "dark" : "light";
  }, [theme]);

  const handleToggleTheme = useCallback(() => {
    try {
      localStorage.setItem(ADMIN_THEME_USER_STORAGE_KEY, "1");
    } catch (_e) {
      // ignore
    }
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const pageMeta = useMemo(() => {
    const meta = resolvePageMeta(location.pathname);
    if (
      isBoutique &&
      (location.pathname.startsWith("/admin/products") ||
        location.pathname.startsWith("/superadmin/products"))
    ) {
      return {
        ...meta,
        title: "Catalogue produits",
        subtitle: "Gérez vos produits, SKU et variations.",
      };
    }
    if (
      isBoutique &&
      (location.pathname.startsWith("/admin/categories") ||
        location.pathname.startsWith("/superadmin/categories"))
    ) {
      return {
        ...meta,
        title: "Catégories produits",
        subtitle: "",
      };
    }
    if (
      isBoutique &&
      (location.pathname.startsWith("/admin/medias") ||
        location.pathname.startsWith("/superadmin/medias"))
    ) {
      return {
        ...meta,
        subtitle: "",
      };
    }
    if (
      isBoutique &&
      (location.pathname.startsWith("/admin/users") ||
        location.pathname.startsWith("/superadmin/users"))
    ) {
      return {
        ...meta,
        subtitle: "Pilotez les rôles caissiers et managers",
      };
    }
    return meta;
  }, [isBoutique, location.pathname]);

  useEffect(() => {
    if (isSuperAdminPath) {
      let parsedSuper = null;
      try {
        const superStored = localStorage.getItem("moo_user_superadmin");
        parsedSuper = superStored ? JSON.parse(superStored) : null;
      } catch (_e) {
        parsedSuper = null;
      }
      setCurrentUser(parsedSuper);
      return;
    }
    let parsed = null;
    try {
      const stored = localStorage.getItem("moo_user");
      parsed = stored ? JSON.parse(stored) : null;
    } catch (_e) {
      parsed = null;
    }
    if (parsed) {
      setCurrentUser(parsed);
      return;
    }
    if (isCloudTenantHostname()) {
      setCurrentUser(null);
      return;
    }
    // Sécurité: ne jamais injecter d'utilisateur/token de démonstration.
    setCurrentUser(null);
  }, [isSuperAdminPath]);

  useEffect(() => {
    if (isSuperAdminPath || !currentUser) return;
    if (!isCloudTenantHostname()) return;

    let cancelled = false;
    const refreshCurrentUserFromServer = async () => {
      try {
        const response = await fetch(`/api/users?_ts=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-store" },
        });
        if (!response.ok) return;
        const users = await response.json().catch(() => []);
        if (!Array.isArray(users) || users.length === 0 || cancelled) return;

        const currentId = Number(currentUser?.id || 0);
        const currentUsername = String(currentUser?.username || "")
          .trim()
          .toLowerCase();
        const matched =
          users.find((user) => Number(user?.id || 0) === currentId) ||
          users.find(
            (user) =>
              String(user?.username || "")
                .trim()
                .toLowerCase() === currentUsername,
          );

        if (!matched) return;
        const currentSignature = JSON.stringify({
          first_name: currentUser?.first_name || "",
          last_name: currentUser?.last_name || "",
          role: currentUser?.role || "",
          username: currentUser?.username || "",
        });
        const matchedSignature = JSON.stringify({
          first_name: matched?.first_name || "",
          last_name: matched?.last_name || "",
          role: matched?.role || "",
          username: matched?.username || "",
        });
        if (currentSignature === matchedSignature) return;

        const mergedUser = {
          ...currentUser,
          ...matched,
        };
        if (cancelled) return;
        setCurrentUser(mergedUser);
        try {
          localStorage.setItem("moo_user", JSON.stringify(mergedUser));
          if (localStorage.getItem("moo_cloud_user")) {
            localStorage.setItem("moo_cloud_user", JSON.stringify(mergedUser));
          }
        } catch (_e) {
          // ignore storage errors
        }
      } catch (_e) {
        // ignore refresh errors
      }
    };

    refreshCurrentUserFromServer();
    const timer = setInterval(refreshCurrentUserFromServer, 120000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [currentUser, isSuperAdminPath]);

  useEffect(() => {
    let mounted = true;
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (!res.ok) return;
        const data = await res.json();
        const nextType = extractBusinessType(data);
        if (mounted) setBusinessType(nextType);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("restaurant-info", JSON.stringify(data));
          } catch (_e) {}
        }
      } catch (_e) {
        // ignore
      }
    };
    loadBusinessType();
    return () => {
      mounted = false;
    };
  }, []);

  const applyAllowedFromStorage = useCallback(() => {
    if (currentUser?.role === "superadmin") {
      setAllowedPages(null);
      return;
    }
    try {
      const rawPackages = localStorage.getItem("moo_packages");
      let activeId = localStorage.getItem("moo_active_package");
      if (!rawPackages) {
        setAllowedPages(null);
        return;
      }
      const list = JSON.parse(rawPackages) || [];
      if (!activeId && list.length > 0) {
        activeId = list[0].id;
        localStorage.setItem("moo_active_package", activeId);
      }
      if (!activeId && list.length > 0) {
        activeId = list[0].id;
        localStorage.setItem("moo_active_package", activeId);
      }
      if (!activeId) {
        setAllowedPages(null);
        return;
      }
      const active = list.find((p) => p.id === activeId);
      if (active && Array.isArray(active.pages)) {
        setAllowedPages(active.pages.map((page) => (page === "keassa" ? "waise" : page)));
      } else {
        setAllowedPages(null);
      }
    } catch (e) {
      setAllowedPages(null);
    }
  }, [currentUser]);

  // Charger le package actif pour filtrer les menus (sauf superadmin)
  useEffect(() => {
    applyAllowedFromStorage();
  }, [currentUser, location.pathname, applyAllowedFromStorage]);

  // Écoute les changements de package activé depuis un autre onglet
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "moo_package_broadcast") {
        // Recharger allowedPages
        applyAllowedFromStorage();
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [applyAllowedFromStorage]);

  // Synchroniser les packages depuis le serveur (pour partage multi-navigateurs)
  useEffect(() => {
    if (currentUser?.role === "superadmin") return;
    const syncPackages = async () => {
      try {
        const res = await fetch(`/api/packages?_ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
          cache: "no-store",
        });
        if (res.ok && res.status !== 304) {
          const data = await res.json();
          const serverPkgs = Array.isArray(data.packages) ? data.packages : [];
          const activeId = data.activeId || serverPkgs[0]?.id || "";
          localStorage.setItem("moo_packages", JSON.stringify(serverPkgs));
          localStorage.setItem("moo_active_package", activeId);
          localStorage.setItem("moo_package_broadcast", `${activeId}_${Date.now()}`);
        }
      } catch (e) {
        console.warn("Sync packages serveur échoué", e);
      } finally {
        applyAllowedFromStorage();
      }
    };
    syncPackages();
  }, [currentUser, applyAllowedFromStorage]);

  const containerBg =
    theme === "dark"
      ? "bg-[#050b18] text-slate-100"
      : "bg-slate-50 text-slate-900";

  const headerBg =
    theme === "dark" ? "bg-white/5 border-white/10" : "bg-white/80 border-slate-200";

  const [cloudLink, setCloudLink] = useState(null);
  const [cloudSyncing, setCloudSyncing] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushServerSyncDone, setPushServerSyncDone] = useState(false);
  const pushBusyRef = useRef(false);
  const pushRetryAttemptRef = useRef(0);
  const pushRetryTimerRef = useRef(null);
  const cloudSalesFallbackRef = useRef({
    primed: false,
    lastKey: null,
  });

  const isCloudPushContext = useMemo(() => {
    if (!currentUser) return false;
    const role = String(currentUser.role || "").toLowerCase();
    if (!["admin", "manager", "superadmin"].includes(role)) return false;
    try {
      const token = (localStorage.getItem("moo_token") || "").trim();
      if (!token) return false;
      if (isCloudTenantHostname()) return true;
      return !!localStorage.getItem("moo_cloud_user");
    } catch (_e) {
      return false;
    }
  }, [currentUser]);

  const canUsePush = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }, []);

  const readCloudToken = useCallback(() => {
    try {
      return localStorage.getItem("moo_token") || "";
    } catch (_e) {
      return "";
    }
  }, []);

  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  const fetchPushPublicKey = useCallback(async () => {
    const token = readCloudToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await apiFetch("/api/cloud/push/public-key", {
      headers,
      includeBearer: !token,
      tokenMode: "admin",
      // Les notifications sont optionnelles : leur indisponibilité ne doit
      // jamais invalider la session principale du tableau de bord.
      clearTokenOn401: false,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const backendError = String(body?.error || "").toLowerCase();
      const isTokenIssue =
        backendError.includes("token manquant") || backendError.includes("token invalide");
      throw new Error(
        isTokenIssue
          ? "Session cloud expirée. Reconnectez-vous."
          : body?.error || "Session cloud expirée. Reconnectez-vous.",
      );
    }
    return body.publicKey;
  }, [readCloudToken]);

  const playCashSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.25);
      oscillator.onended = () => ctx.close();
    } catch (_e) {
      // ignore audio errors
    }
  }, []);

  const subscribeToPush = useCallback(
    async ({ requestPermission = false, silent = false } = {}) => {
      if (!isCloudPushContext || !canUsePush || pushBusyRef.current) return false;
      pushBusyRef.current = true;
      setPushBusy(true);
      try {
        let permission = Notification.permission;
        if (requestPermission) {
          permission = await Notification.requestPermission();
        }
        setPushPermission(permission || "default");
        if (permission !== "granted") {
          if (!silent) showToast("error", "Autorisation notifications refusée");
          return false;
        }

        const registration = await navigator.serviceWorker.register("/moo-push-sw.js");
        await registration.update().catch(() => {});

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          const publicKey = await fetchPushPublicKey();
          const appServerKey = urlBase64ToUint8Array(publicKey);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appServerKey,
          });
        }

        const token = readCloudToken();
        const deviceLabel =
          navigator.userAgentData?.platform || navigator.platform || "web";

        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const res = await apiFetch("/api/cloud/push/subscribe", {
          method: "POST",
          headers,
          includeBearer: !token,
          tokenMode: "admin",
          clearTokenOn401: false,
          body: JSON.stringify({
            subscription,
            userId: currentUser?.id || null,
            role: currentUser?.role || null,
            deviceLabel,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const backendError = String(body?.error || "").toLowerCase();
          const isTokenIssue =
            backendError.includes("token manquant") || backendError.includes("token invalide");
          throw new Error(
            isTokenIssue
              ? "Session cloud expirée. Reconnectez-vous."
              : body?.error || "Session cloud expirée. Reconnectez-vous.",
          );
        }

        setPushSubscribed(true);
        if (!silent) showToast("success", "Notifications activées");
        return true;
      } catch (e) {
        if (!silent) showToast("error", e.message || "Impossible d'activer les notifications");
        return false;
      } finally {
        pushBusyRef.current = false;
        setPushBusy(false);
      }
    },
    [
      isCloudPushContext,
      canUsePush,
      readCloudToken,
      fetchPushPublicKey,
      urlBase64ToUint8Array,
      currentUser,
    ],
  );

  const isCloudHost = useMemo(() => isCloudTenantHostname(), []);
  const showCaisseShortcut = !isCloudHost && basePath === "/admin";
  const isUsersPage =
    location.pathname === `${basePath}/users` ||
    location.pathname.startsWith(`${basePath}/users/`);
  const isTablesPage =
    location.pathname === `${basePath}/tables` ||
    location.pathname.startsWith(`${basePath}/tables/`);
  const blockUsersOnCloud = isCloudHost && isUsersPage;
  const blockTablesOnCloud = isCloudHost && isTablesPage;

  const buildTag = APP_VERSION ? `v${APP_VERSION}` : "";

  useEffect(() => {
    if (isCloudHost) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/superadmin/cloud-link", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (!cancelled) setCloudLink(data);
      } catch (_e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCloudHost, location.pathname]);

  const handleManualCloudSync = useCallback(async () => {
    if (cloudSyncing) return;
    setCloudSyncing(true);
    try {
      const res = await fetch("/api/superadmin/cloud-sync/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope: "full" }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok === false) {
        throw new Error(body?.error || body?.details || "Sync cloud échouée");
      }
      showToast("success", "Synchronisation cloud lancée.");
    } catch (e) {
      showToast("error", e.message || "Synchronisation cloud impossible.");
    } finally {
      setCloudSyncing(false);
    }
  }, [cloudSyncing]);

  const cloudConnected = !!cloudLink?.activation_token;
  const showPushButton = isCloudPushContext && canUsePush;
  const pushButtonLabel = pushSubscribed
    ? "Notifications actives"
    : pushPermission === "denied"
      ? "Notifications bloquées"
      : "Activer notifications";
  const pushButtonDisabled = pushBusy || pushPermission === "denied";

  useEffect(() => {
    if (!isCloudPushContext || !canUsePush) return;
    setPushServerSyncDone(false);
  }, [isCloudPushContext, canUsePush, currentUser?.id]);

  useEffect(() => {
    if (!isCloudPushContext || !canUsePush) return;
    setPushPermission(Notification.permission || "default");
    let cancelled = false;
    (async () => {
      try {
        const registration =
          (await navigator.serviceWorker.getRegistration("/moo-push-sw.js")) ||
          (await navigator.serviceWorker.getRegistration());
        const subscription = registration
          ? await registration.pushManager.getSubscription()
          : null;
        if (!cancelled) setPushSubscribed(Boolean(subscription));
      } catch (_e) {
        if (!cancelled) setPushSubscribed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCloudPushContext, canUsePush]);

  useEffect(() => {
    if (!isCloudPushContext || !canUsePush) return;
    if (pushPermission !== "granted") return;
    if (pushSubscribed || pushBusy) return;
    subscribeToPush({ requestPermission: false, silent: true });
  }, [
    isCloudPushContext,
    canUsePush,
    pushPermission,
    pushSubscribed,
    pushBusy,
    subscribeToPush,
  ]);

  // Android/PWA: même si le navigateur a déjà une subscription locale,
  // on la resynchronise côté serveur une fois pour éviter les subscriptions orphelines.
  useEffect(() => {
    if (!isCloudPushContext || !canUsePush) return;
    if (pushPermission !== "granted") return;
    if (!pushSubscribed || pushBusy || pushServerSyncDone) return;
    let cancelled = false;
    (async () => {
      const ok = await subscribeToPush({ requestPermission: false, silent: true });
      if (!cancelled && ok) {
        setPushServerSyncDone(true);
        pushRetryAttemptRef.current = 0;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isCloudPushContext,
    canUsePush,
    pushPermission,
    pushSubscribed,
    pushBusy,
    pushServerSyncDone,
    subscribeToPush,
  ]);

  useEffect(() => {
    if (pushRetryTimerRef.current) {
      clearTimeout(pushRetryTimerRef.current);
      pushRetryTimerRef.current = null;
    }
    if (!isCloudPushContext || !canUsePush) return undefined;
    if (pushPermission !== "granted") return undefined;
    if (pushBusy) return undefined;
    if (pushSubscribed && pushServerSyncDone) {
      pushRetryAttemptRef.current = 0;
      return undefined;
    }

    const attempt = pushRetryAttemptRef.current;
    const delayMs = Math.min(60000, Math.max(1500, 2000 * Math.pow(2, attempt)));
    pushRetryTimerRef.current = window.setTimeout(async () => {
      const ok = await subscribeToPush({ requestPermission: false, silent: true });
      if (ok) {
        setPushServerSyncDone(true);
        pushRetryAttemptRef.current = 0;
      } else {
        pushRetryAttemptRef.current = Math.min(attempt + 1, 8);
      }
    }, delayMs);

    return () => {
      if (pushRetryTimerRef.current) {
        clearTimeout(pushRetryTimerRef.current);
        pushRetryTimerRef.current = null;
      }
    };
  }, [
    isCloudPushContext,
    canUsePush,
    pushPermission,
    pushSubscribed,
    pushServerSyncDone,
    pushBusy,
    subscribeToPush,
  ]);

  useEffect(() => {
    if (!isCloudPushContext || !canUsePush) return;
    const handler = (event) => {
      const payload = event?.data?.payload || {};
      if (event?.data?.type !== "moo-payment") return;
      if (document.visibilityState === "visible") {
        const message = payload?.body || "Nouvelle vente enregistrée.";
        showToast("success", message);
        playCashSound();
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => {
      navigator.serviceWorker?.removeEventListener("message", handler);
    };
  }, [isCloudPushContext, canUsePush, playCashSound]);

  useEffect(() => {
    const pushActive =
      pushPermission === "granted" && pushSubscribed && pushServerSyncDone;
    const shouldEnableFallback = isCloudHost && isCloudPushContext && !pushActive;
    if (!shouldEnableFallback) {
      cloudSalesFallbackRef.current.primed = false;
      return undefined;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`/api/sales?page=1&limit=1&fast=1&_ts=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-store" },
        });
        if (!response.ok) return;
        const body = await response.json().catch(() => ({}));
        const sale = Array.isArray(body?.sales) && body.sales.length > 0 ? body.sales[0] : null;
        if (!sale) return;
        const saleKey = [
          String(sale?.id || ""),
          String(sale?.payment_number || ""),
          String(sale?.updated_at || sale?.created_at || ""),
        ].join("|");
        const ref = cloudSalesFallbackRef.current;
        if (!ref.primed) {
          ref.primed = true;
          ref.lastKey = saleKey;
          return;
        }
        if (ref.lastKey && ref.lastKey !== saleKey && !cancelled) {
          ref.lastKey = saleKey;
          showToast("success", "Nouvelle vente enregistrée.");
          playCashSound();
          return;
        }
        ref.lastKey = saleKey;
      } catch (_e) {
        // fallback silencieux
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    isCloudHost,
    isCloudPushContext,
    pushPermission,
    pushSubscribed,
    pushServerSyncDone,
    playCashSound,
  ]);

  const computedSections = useMemo(
    () =>
      buildNavSections(basePath, allowedPages, {
        hideUsers: isCloudHost,
        hideTables: isCloudHost || isBoutique,
      }),
    [basePath, allowedPages, isCloudHost, isBoutique],
  );

  const [installPrompt, setInstallPrompt] = useState(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const headerRef = useRef(null);
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(0);
  const isInstalled = isPwaStandalone();
  const canInstall = useMemo(() => {
    if (isInstalled) return false;
    return Boolean(installPrompt) || isIosDevice();
  }, [installPrompt, isInstalled]);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      // Chrome/Edge Android
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onAppInstalled = () => {
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    try {
      if (installPrompt?.prompt) {
        await installPrompt.prompt();
        await installPrompt.userChoice?.catch(() => {});
        setInstallPrompt(null);
        return;
      }
      if (isIosDevice()) {
        setInstallHelpOpen(true);
      }
    } catch (_e) {
      // ignore
    }
  }, [installPrompt]);

  const checkCurrentSessionHasSales = useCallback(async () => {
    if (!currentUser?.id) return false;
    try {
      const activeResponse = await fetch(`/api/cash-sessions/active`);
      if (!activeResponse.ok) return false;
      const activeSession = await activeResponse.json();
      const sessionId = activeSession?.id;
      if (!sessionId) return false;
      const summaryResponse = await fetch(
        `/api/cash-journal/summary/${sessionId}?cashierId=${currentUser.id}`,
      );
      if (!summaryResponse.ok) return false;
      const summaryData = await summaryResponse.json();
      const ordersCount = Number(summaryData?.summary?.total_orders || 0);
      const revenue = Number(summaryData?.summary?.total_revenue || 0);
      return ordersCount > 0 || revenue > 0;
    } catch (_e) {
      return false;
    }
  }, [currentUser?.id]);

  const closeActiveCashSessionOnLogout = useCallback(async () => {
    if (
      !currentUser?.id ||
      !canUseCashSessionForRole(currentUser?.role)
    ) return;
    const activeResponse = await fetch(
      `/api/cash-sessions/active`,
      { cache: "no-store" },
    );
    if (activeResponse.status === 404) return;
    if (!activeResponse.ok) {
      throw new Error("Impossible de vérifier la session de caisse active");
    }
    const activeSession = await activeResponse.json();
    if (!activeSession?.id) return;

    const summaryResponse = await fetch(
      `/api/cash-journal/summary/${activeSession.id}?cashierId=${currentUser.id}`,
      { cache: "no-store" },
    );
    if (!summaryResponse.ok) {
      throw new Error("Impossible de calculer la clôture de caisse");
    }
    const summaryData = await summaryResponse.json();
    const theoretical =
      summaryData?.summary?.theoretical_balance ??
      (Number(summaryData?.summary?.total_revenue || 0) +
        Number(activeSession.initial_amount || 0));
    const computedFinalAmount = Number(theoretical);
    if (!Number.isFinite(computedFinalAmount)) {
      throw new Error("Montant de clôture de caisse invalide");
    }

    const closeResponse = await fetch(`/api/cash-sessions/${activeSession.id}/close`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        finalAmount: Math.max(0, Math.round(computedFinalAmount)),
        notes: "Session fermée lors de la déconnexion",
      }),
    });
    if (!closeResponse.ok) {
      const payload = await closeResponse.json().catch(() => null);
      throw new Error(
        payload?.error || payload?.message || "La session de caisse n'a pas été fermée",
      );
    }
  }, [currentUser?.id, currentUser?.role]);

  const handleRequestLogout = useCallback(async () => {
    if (logoutDialogLoading || logoutDialogOpen || logoutConfirmLoading) return;
    setLogoutError("");
    setLogoutDialogLoading(true);
    try {
      const hasSales = await checkCurrentSessionHasSales();
      setLogoutSalesWarning(hasSales);
      setLogoutDialogOpen(true);
    } finally {
      setLogoutDialogLoading(false);
    }
  }, [checkCurrentSessionHasSales, logoutConfirmLoading, logoutDialogLoading, logoutDialogOpen]);

  const handleConfirmLogout = useCallback(async () => {
    if (logoutConfirmLoading) return;
    setLogoutConfirmLoading(true);
    setLogoutError("");
    const isSuper = isSuperAdminPath || currentUser?.role === "superadmin";
    const tokenKey = isSuper ? "moo_token_superadmin" : "moo_token";
    const userKey = isSuper ? "moo_user_superadmin" : "moo_user";
    const existingToken = localStorage.getItem(tokenKey);
    try {
      await closeActiveCashSessionOnLogout();
    } catch (_error) {
      setLogoutError(
        "La session de caisse n'a pas pu être fermée. Réessayez avant de vous déconnecter.",
      );
      setLogoutConfirmLoading(false);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${existingToken || ""}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (_e) {
      // Une fois la caisse fermée, une indisponibilité du endpoint d'authentification
      // ne doit pas empêcher d'effacer le jeton de ce poste.
    }

    if (currentUser?.id) {
      clearLockedSession(currentUser.id);
      try {
        sessionStorage.removeItem(`moo_admin_cash_drawer_shown_${currentUser.id}`);
      } catch (_e) {}
    }
    try {
      const cloudToken = localStorage.getItem("moo_cloud_token");
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(userKey);
      if (cloudToken && cloudToken === existingToken) {
        localStorage.removeItem("moo_cloud_token");
        localStorage.removeItem("moo_cloud_user");
        window.dispatchEvent(new Event("cloud-auth-changed"));
      }
    } catch (_e) {}
    window.dispatchEvent(new Event("moo-auth-refresh"));
    navigate(isSuper ? "/superadmin" : "/", { replace: true });
    setLogoutDialogOpen(false);
    setLogoutConfirmLoading(false);
  }, [
    closeActiveCashSessionOnLogout,
    currentUser?.id,
    currentUser?.role,
    isSuperAdminPath,
    logoutConfirmLoading,
    navigate,
  ]);

  const handleCancelLogout = useCallback(() => {
    if (logoutConfirmLoading) return;
    setLogoutError("");
    setLogoutDialogOpen(false);
  }, [logoutConfirmLoading]);

  const handleLockSession = useCallback(async () => {
    const isSuper = isSuperAdminPath || currentUser?.role === "superadmin";
    const tokenKey = isSuper ? "moo_token_superadmin" : "moo_token";
    const token = localStorage.getItem(tokenKey);

    if (currentUser?.id) {
      setLockedSession(currentUser.id, {
        currentUser,
        timestamp: new Date().toISOString(),
        lockReason: "manual",
        source: "admin-layout",
      });
    }

    try {
      localStorage.removeItem("moo_user");
      localStorage.removeItem("moo_token");
      localStorage.removeItem("moo_user_superadmin");
      localStorage.removeItem("moo_token_superadmin");
      window.dispatchEvent(new Event("moo-auth-refresh"));
    } catch (_e) {
      // ignore
    }

    if (token) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1500);
        fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
          signal: controller.signal,
        })
          .catch(() => {})
          .finally(() => clearTimeout(timeoutId));
      } catch (_e) {
        // ignore
      }
    }

    setIsMobileSidebarOpen(false);
    navigate(isSuper ? "/superadmin" : "/", { replace: true });
  }, [currentUser, isSuperAdminPath, navigate]);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return;

    const updateHeaderHeight = () => {
      const next = Math.ceil(headerElement.getBoundingClientRect().height || 0);
      setMobileHeaderHeight(next);
    };

    updateHeaderHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(headerElement);
    }

    window.addEventListener("resize", updateHeaderHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateHeaderHeight);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateHeaderHeight);
      }
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const mobileTabs = useMemo(() => {
    const isAllowed = (pageId) =>
      currentUser?.role === "superadmin" ||
      !allowedPages ||
      allowedPages.length === 0 ||
      allowedPages.includes(pageId);

    const tabs = [
      { id: "dashboard", label: "Accueil", icon: Home, to: basePath },
      { id: "sales", label: "Ventes", icon: TrendingUp, to: `${basePath}/sales` },
      { id: "reports", label: "Statistiques", icon: BarChart2, to: `${basePath}/reports` },
      { id: "products", label: "Produits", icon: ShoppingBag, to: `${basePath}/products` },
      { id: "settings", label: "Réglages", icon: Settings, to: `${basePath}/settings` },
    ].filter((t) => isAllowed(t.id));

    // Always keep at least 3 tabs + "Plus"
    return tabs.slice(0, 4);
  }, [allowedPages, basePath, currentUser?.role]);

  return (
    <div
      className={`flex w-full min-w-0 max-w-[100vw] min-h-[100dvh] h-[100dvh] overflow-hidden ${containerBg} transition-colors sm:min-h-screen sm:h-screen`}
    >
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
          <AdminSidebar
            collapsed={isSidebarCollapsed}
            theme={theme}
            onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
            onToggleTheme={handleToggleTheme}
            onLogout={handleRequestLogout}
            onLockSession={handleLockSession}
            logoSrc={LOGO_SRC}
            currentUser={currentUser}
            allowedPages={allowedPages}
            basePath={basePath}
            showCaisseShortcut={showCaisseShortcut}
            hideUsers={isCloudHost}
            hideTables={isCloudHost || isBoutique}
          />
      </div>

      {/* Mobile: bottom sheet menu */}
      <div className="lg:hidden">
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setIsMobileSidebarOpen(false)}
            role="presentation"
          />
        )}
        <div
          className={`fixed inset-x-0 bottom-0 z-50 transform transition-transform duration-300 ${
            isMobileSidebarOpen ? "translate-y-0" : "translate-y-full"
          }`}
          aria-hidden={!isMobileSidebarOpen}
        >
          <div className="mx-auto w-full max-w-2xl rounded-t-3xl border border-white/10 bg-[#0b1220]/95 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <div className="flex items-center gap-3">
                <img
                  src={LOGO_SRC}
                  alt=""
                  className="h-9 w-9 rounded-2xl border border-white/15 bg-white/10 object-contain p-1"
                />
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-white/60">
                    Administration
                  </div>
                  <div className="text-sm font-semibold text-white">
                    {currentUser?.first_name || currentUser?.username || "MOO Sync"}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white/80"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={handleToggleTheme}
                  className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/90"
                >
                  {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                  {theme === "dark" ? "Thème clair" : "Thème sombre"}
                </button>
              </div>

              <div className="mt-4 max-h-[55vh] overflow-y-auto pr-1">
                {computedSections.map((section) => (
                  <div key={section.title} className="mb-4">
                    <div className="px-2 pb-2 text-xs uppercase tracking-[0.3em] text-white/45">
                      {section.title}
                    </div>
                    <div className="space-y-2">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={() => setIsMobileSidebarOpen(false)}
                            className={({ isActive }) =>
                              [
                                "flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold transition",
                                isActive
                                  ? "border-amber-400/40 bg-amber-400/10 text-white"
                                  : "border-white/10 bg-white/5 text-white/85 hover:bg-white/10",
                              ].join(" ")
                            }
                          >
                            <span className="flex items-center gap-3">
                              <Icon size={18} className="opacity-90" />
                              {item.label}
                            </span>
                            {item.badge ? (
                              <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-200">
                                {item.badge}
                              </span>
                            ) : null}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileSidebarOpen(false);
                      handleRequestLogout();
                    }}
                    className="flex-1 flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white/80"
                    disabled={logoutDialogLoading}
                  >
                    <LogOut size={16} />
                    Déconnexion
                  </button>
                  <button
                    type="button"
                    onClick={handleLockSession}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10"
                    title="Verrouiller la session"
                    aria-label="Verrouiller la session"
                  >
                    <Lock size={16} />
                  </button>
                </div>
              </div>
            </div>
            <div className="h-[env(safe-area-inset-bottom)]" />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex w-full min-w-0 flex-1 min-h-0 flex-col">
        <header
          ref={headerRef}
          className={`fixed inset-x-0 top-0 z-20 backdrop-blur-xl ${headerBg} sm:sticky sm:inset-x-auto`}
        >
          <div className="px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-3 sm:px-6 sm:py-4">
            <div className="flex flex-wrap items-center justify-between gap-3 sm:flex-nowrap">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="lg:hidden">
                  <img
                    src={LOGO_SRC}
                    alt=""
                    className="h-10 w-10 rounded-2xl border border-white/15 bg-white/10 object-contain p-1"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-slate-400">
                    {pageMeta.kicker}
                  </p>
                  <h1
                    className={`truncate font-semibold sm:text-xl ${
                      pageMeta.title === "Administration MOO Sync" ? "text-sm" : "text-base"
                    }`}
                  >
                    {pageMeta.title}
                  </h1>
                  {pageMeta.subtitle && (
                    <p className="mt-0.5 hidden text-sm text-slate-500 dark:text-slate-400 sm:block">
                      {pageMeta.subtitle}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {showPushButton && (
                  <button
                    type="button"
                    onClick={() => subscribeToPush({ requestPermission: true })}
                    disabled={pushButtonDisabled}
                    title={
                      pushPermission === "denied"
                        ? "Notifications bloquées dans le navigateur"
                        : pushButtonLabel
                    }
                    className={`inline-flex h-10 items-center gap-2 rounded-2xl border px-3 text-xs font-semibold transition ${
                      pushSubscribed
                        ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                        : pushPermission === "denied"
                          ? "border-red-400/30 bg-red-400/10 text-red-200"
                          : theme === "dark"
                            ? "border-white/20 bg-white/10 text-white/80 hover:bg-white/15"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    } ${pushButtonDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                    aria-label={pushButtonLabel}
                  >
                    <Bell size={16} />
                    <span className="hidden sm:inline">{pushButtonLabel}</span>
                  </button>
                )}
                {!isCloudHost && buildTag ? (
                  <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold text-white/60">
                    {buildTag}
                  </span>
                ) : null}
                {canInstall && (
                  <button
                    type="button"
                    onClick={handleInstall}
                    className="hidden h-10 w-10 items-center justify-center rounded-2xl border border-amber-400/30 bg-amber-400/10 text-amber-200 sm:inline-flex"
                    title="Installer l’app"
                    aria-label="Installer l’app"
                  >
                    <Download size={18} />
                  </button>
                )}
                {!isCloudHost && (
                  <button
                    type="button"
                    onClick={handleManualCloudSync}
                    disabled={!cloudConnected || cloudSyncing}
                    title={
                      cloudConnected
                        ? "Synchroniser vers le cloud"
                        : "Établissement non connecté au cloud"
                    }
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition ${
                      cloudConnected
                        ? theme === "dark"
                          ? "border-white/20 bg-white/10 text-white hover:bg-white/15"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        : "border-white/10 bg-white/5 text-white/40 cursor-not-allowed"
                    } ${cloudSyncing ? "opacity-70" : ""}`}
                    aria-label="Synchroniser vers le cloud"
                  >
                    <Cloud size={18} className={cloudConnected ? "" : "opacity-50"} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="sm:hidden" style={{ height: mobileHeaderHeight }} />

        <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto p-4 pb-[calc(env(safe-area-inset-bottom)+8rem)] sm:p-6 sm:pb-[calc(env(safe-area-inset-bottom)+7rem)] lg:pb-6">
          {blockUsersOnCloud || blockTablesOnCloud ? (
            <div
              className={`w-full min-w-0 max-w-full rounded-3xl border shadow-lg ${
                theme === "dark"
                  ? "border-white/10 bg-white/5 text-slate-100"
                  : "border-white bg-white text-slate-800"
              } p-6 text-center`}
            >
              <p className="text-4xl mb-4">🚫</p>
              <h2 className="text-xl font-semibold mb-2">Page indisponible sur le cloud</h2>
              <p className={theme === "dark" ? "text-slate-300" : "text-slate-600"}>
                {blockUsersOnCloud
                  ? "La gestion des utilisateurs se fait uniquement en local."
                  : "La gestion des tables se fait uniquement en local."}
              </p>
            </div>
          ) : currentUser?.role !== "superadmin" &&
          allowedPages &&
          allowedPages.length > 0 &&
          !allowedPages.includes(getPageIdFromPath(location.pathname.replace(basePath, "/admin"))) ? (
            <div
              className={`w-full min-w-0 max-w-full rounded-3xl border shadow-lg ${
                theme === "dark"
                  ? "border-white/10 bg-white/5 text-slate-100"
                  : "border-white bg-white text-slate-800"
              } p-6 text-center`}
            >
              <p className="text-4xl mb-4">🚫</p>
              <h2 className="text-xl font-semibold mb-2">Vous n'avez pas accès à cette page</h2>
              <p className={theme === "dark" ? "text-slate-300" : "text-slate-600"}>
                Contactez l'équipe de Djizef SARL pour activer cette fonctionnalité via un package adapté.
              </p>
            </div>
          ) : (
            <div
              className={`w-full min-w-0 max-w-full rounded-3xl border shadow-lg ${
                theme === "dark"
                  ? "border-white/10 bg-white/5"
                  : "border-white bg-white"
              }`}
            >
              <div className="w-full min-w-0 max-w-full rounded-3xl p-2 sm:p-4 overflow-hidden">
                <Outlet />
              </div>
            </div>
          )}
        </main>

        {/* Mobile bottom navigation */}
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#0b1220]/85 backdrop-blur-xl lg:hidden">
          <div className="mx-auto flex w-full max-w-2xl items-center justify-around px-2 py-2">
            {mobileTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <NavLink
                  key={tab.id}
                  to={tab.to}
                  className={({ isActive }) =>
                    [
                      "flex flex-1 min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-xs font-semibold transition",
                      isActive ? "text-amber-200" : "text-white/70 hover:text-white",
                    ].join(" ")
                  }
                  end={tab.id === "dashboard"}
                >
                  <Icon size={18} />
                  <span className="truncate">{tab.label}</span>
                </NavLink>
              );
            })}
            <button
              type="button"
              onClick={() => setIsMobileSidebarOpen(true)}
              className="flex flex-1 min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-xs font-semibold text-white/70 transition hover:text-white"
              aria-label="Plus"
            >
              <MoreHorizontal size={18} />
              <span className="truncate">Plus</span>
            </button>
          </div>
          <div className="h-[env(safe-area-inset-bottom)]" />
        </nav>
      </div>

      {/* iOS install help */}
      {logoutDialogOpen && (
        <div
          className="fixed inset-0 z-[65] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm lg:items-center"
          role="presentation"
          onClick={handleCancelLogout}
        >
          <div
            className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0b1220]/95 p-5 text-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-semibold">Déconnexion de la session</div>
                <div className="mt-1 text-sm text-white/70">
                  {logoutSalesWarning
                    ? "Vous avez enregistré des ventes en caisse pendant cette session."
                    : "Voulez-vous vraiment vous déconnecter ?"}
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelLogout}
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white/80"
                aria-label="Fermer"
                disabled={logoutConfirmLoading}
              >
                <X size={18} />
              </button>
            </div>

            {logoutSalesWarning && (
              <p className="mt-4 rounded-2xl border border-amber-300/25 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
                Une nouvelle connexion redémarrera votre journal de caisse. Avant de confirmer, il est
                recommandé de tirer votre rapport X pour votre contrôle de fin de journée.
              </p>
            )}

            {logoutError && (
              <p className="mt-4 rounded-2xl border border-red-300/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                {logoutError}
              </p>
            )}

            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleCancelLogout}
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white/80 transition hover:bg-white/10"
                disabled={logoutConfirmLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmLogout}
                className="inline-flex items-center justify-center rounded-2xl border border-red-300/40 bg-red-500/15 px-4 py-2.5 text-sm font-semibold text-red-100 transition hover:bg-red-500/25"
                disabled={logoutConfirmLoading}
              >
                {logoutConfirmLoading ? "Déconnexion..." : "Confirmer la déconnexion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {installHelpOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm lg:items-center"
          role="presentation"
          onClick={() => setInstallHelpOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0b1220]/95 p-5 text-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-semibold">Installer l’app</div>
                <div className="mt-1 text-sm text-white/70">
                  Sur iPhone, utilisez “Ajouter à l’écran d’accueil”.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setInstallHelpOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white/80"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>
            <ol className="mt-4 space-y-2 text-sm text-white/80">
              <li>1. Ouvrez le menu de partage du navigateur (icône “partager”).</li>
              <li>2. Choisissez “Sur l’écran d’accueil”.</li>
              <li>3. Confirmez “Ajouter”.</li>
            </ol>
            <div className="mt-4 text-xs text-white/55">
              Astuce: sur le cloud, l’installation est recommandée pour une expérience mobile plus fluide.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
